<template>
  <BasePageContainer class="admin member-add pt-4">
    <div class="page__head admin-head fixed-top p-3">
      <h1 class="admin-head__heading">
        <router-link
          to="/settings/"
          class="admin-head__back">
          <span class="material-icons">
            arrow_back_ios
          </span>
        </router-link>
        ユーザー管理
      </h1>
    </div>
    <div class="page__body admin-content pt-8">
      <SearchUserForm
        @submit="searchAndScrollToMember" />

      <div
        v-if="departments"
        class="admin-members">
        <ul
          v-for="department in departments"
          :key="department.id"
          class="members__body list-unstyled">
          <DepartmentListItemMenu
            :id="department.id"
            :ref="department.id"
            :department="department"
            :show-members="isShowedDepartment(department.id)"
            class="mb-4"
            @click.native="onClickDepartmentListItemMenu(department.id)">
            <DepartmentMemberListItem
              v-for="member in membersByDepartmentId[department.id]"
              :ref="member.id"
              :key="member.id"
              :member="member"
              is-admin
              :active="member.id === previousMemberId"
              :is-searched="isSearched(member)" />
          </DepartmentListItemMenu>
        </ul>
      </div>
    </div>
    <ResultListScroller
      v-if="searchedMembers.length"
      :counts="searchedMembers.length"
      @onClickPrev="scrollToPrev"
      @onClickNext="scrollToNext"
      @onClickClose="resetSearchResult" />
  </BasePageContainer>
</template>

<script>
import DepartmentListItemMenu from '@/components/view/DepartmentListItemMenu.vue'
import DepartmentMemberListItem from '@/components/view/DepartmentMemberListItem.vue'
import SearchUserForm from '@/components/view/SearchUserForm.vue'
import ResultListScroller from '@/components/parts/ResultListScroller.vue'
import { groupBy, uniq } from 'lodash'

export default {
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // メンバー詳細の画面から遷移してきた場合はメンバーにスクロールさせるために記憶する
      if (from.name === 'adminMembersMemberEdit' && from.params.id) {
        vm.previousMemberId = from.params.id
      }
    })
  },
  components: {
    DepartmentListItemMenu,
    DepartmentMemberListItem,
    SearchUserForm,
    ResultListScroller
  },
  data() {
    return {
      members: [],
      departments: [],
      showedDepartmentIds: [],
      membersByDepartmentId: {},
      searchedMembers: [],
      currentSearchedMembersIndex: 0,
      previousMemberId: null
    }
  },
  computed: {
    departmentIds() {
      return this.departments.map(department => department.id)
    },
    // $elがあるため、レンダリング終了まで計算が終わらない -> $nextTickでラップする必要がある
    sortedSearchedMembers() {
      return [...this.searchedMembers].sort((a, b) => {
        const aTop = this.$refs[a.id][0].$el.getBoundingClientRect().top
        const bTop = this.$refs[b.id][0].$el.getBoundingClientRect().top
        if (aTop <= bTop) {
          return -1
        } else {
          return 1
        }
      })
    },
    searchedMembersDepartmentIds() {
      return uniq(this.searchedMembers.map(member => member.department.id))
    },
    memberByMemberId() {
      return this.members.reduce((prev, curr) => {
        return { ...prev, [curr.id]: curr }
      }, {})
    }
  },
  async created() {
    this.$store.commit('startLoading')

    await Promise.all([
      this.fetchDepartments(),
      this.fetchMembers()
    ])

    this.showedDepartmentIds = this.departments.map(department => department.id)

    // メンバー詳細の画面から遷移してきた場合はメンバーにスクロールさせる
    if (this.previousMemberId) {
      const member = this.memberByMemberId[this.previousMemberId]
      if (member) {
        this.$nextTick(() => {
          this.scrollToMember(member)
        })
      }
    }
    this.$store.commit('finishLoading')
  },
  methods: {
    fetchDepartments() {
      return this.$axios.get('/departments/?ordering=-sort_no')
        .then(res => {
          this.departments = res.data.results
        })
        .catch(() => {
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
          this.$store.commit('finishLoading')
          return
        })
    },
    fetchMembers() {
      return this.$axios.get('/accounts/manage_staff/', {
        params: {
          ordering: '-sort_no',
          limit: 99999
        }
      }).then(res => {
        const members = res.data.results
        this.members = members
        this.membersByDepartmentId = groupBy(members, member => member.department?.id)
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        this.$store.commit('finishLoading')
        return
      })
    },
    fetchMembersByDepartmentId(departmentId) {
      return this.$axios.get('/accounts/manage_staff/', {
        params: {
          ordering: '-sort_no',
          limit: 99999,
          department__in: departmentId,
        }
      }).then(res => {
        this.$set(this.membersByDepartmentId, departmentId, res.data.results)
      })
    },
    isShowedDepartment(departmentId) {
      return this.showedDepartmentIds.includes(departmentId)
    },
    onClickDepartmentListItemMenu(departmentId) {
      if (this.showedDepartmentIds.includes(departmentId)) {
        this.showedDepartmentIds = this.showedDepartmentIds.filter(showedDepartmentId => showedDepartmentId !== departmentId)
      } else {
        this.showedDepartmentIds.push(departmentId)
      }
    },
    async searchAndScrollToMember(searchKeyword) {
      // メンバーを検索する
      const res = await this.$axios.get('/accounts/manage_staff/', {
        params: {
          search: searchKeyword,
          ordering: '-sort_no'
        }
      }).catch(error => {
        console.error(error)
        this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合はログインページ下のアプリ更新を押してください。'})
      })

      this.searchedMembers = res.data.results

      if (this.searchedMembersDepartmentIds.length === 0) {
        this.$store.commit('notify/set', {message: 'メンバーが見つかりませんでした'})
        return
      }

      await Promise.all(
        this.searchedMembersDepartmentIds.map(departmentId => this.fetchMembersByDepartmentId(departmentId))
      )
      // メンバーが所属するグループを全て展開
      this.showedDepartmentIds = Array.from(new Set([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds]))
      
      // 検索されたメンバーのうち、画面上での最初のメンバーの要素にスクロール
      this.$nextTick(() => {
        const topMember = this.searchedMembers.reduce((prev, curr) => {
          const currentTop = this.$refs[curr.id][0].$el.getBoundingClientRect().top
          const prevTop = this.$refs[prev.id][0].$el.getBoundingClientRect().top
          return currentTop < prevTop ? curr : prev
        }, this.searchedMembers[0])
        this.scrollToMember(topMember)
      })
    },
    scrollToMember(member) {
      const memberEl = this.$refs[member.id][0].$el
      const targetTop = memberEl.getBoundingClientRect().top + window.pageYOffset - 60
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    isSearched(member) {
      return this.searchedMembers.map(member => member.id).includes(member.id)
    },
    scrollToPrev() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = this.currentSearchedMembersIndex === 0 ? this.searchedMembers.length - 1 : this.currentSearchedMembersIndex - 1
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    scrollToNext() {
      this.showedDepartmentIds = uniq([...this.showedDepartmentIds, ...this.searchedMembersDepartmentIds])
      this.currentSearchedMembersIndex = (this.currentSearchedMembersIndex + 1) % this.searchedMembers.length
      this.$nextTick(() => {
        this.scrollToMember(this.sortedSearchedMembers[this.currentSearchedMembersIndex])
      })
    },
    resetSearchResult() {
      this.searchedMembers = []
    },
  }
}
</script>

<style lang="scss" scoped>
.admin{
  padding-bottom: calc(16px + constant(safe-area-inset-bottom));
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

.body__search {
  position: relative;
  width: 50%;
  margin: 5px 0 15px auto;

  &__icon {
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
  }

  .form-control--search {
    height: 36px;
  }
}

.admin-head{
  background-color: $l-blue;
  @media screen and (min-width:992px) {
    right: 0;
    left: 120px;
  }
  &__back{
    height: 22px;
    width: auto;
    color: $white;
    display: inline-block;
    vertical-align: text-bottom;
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
  }
}

.admin-licence{
  &__number{
    display: inline-block;
    font-size: $fs-xl;
    font-weight: bold;
  }
  &__icon{
    display: inline-block;
    font-size: $fs-xl;
    vertical-align: text-bottom;
  }
}

.admin-members{
  &__icon{
    color: $white;
    vertical-align: middle;
  }
  &__item{
    position: relative;
    background-color: $white;
    border-radius: 8px;
  }
  
  &__btn{
    position: relative;
    background-color: $l-blue;
    box-shadow: 1px 2px 6px $grey05;
  }
  &__btn-img{
    width: 60px;
    height: auto;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    top: 40%;
  }
  
}

.admin-member{
  position: relative;
  &__img{
    object-fit: cover; 
    width: 60px;
    height: 60px;
    max-width: 60px;
  }
  &__name{
    font-weight: bold;
    font-size: $fs-lg;
  }
  &__title{
    font-size: $fs-s;
    line-height: 150%;
    font-weight: bold;
  }
  &__team{
    font-size: $fs-s;
    line-height: 150%;
    font-weight: bold;
  }
  &__icon{
    color: $white;
    vertical-align: middle;
  }
  &__item{
    position: relative;
  }

  &__btn{
    position: relative;
    background-color: $l-blue;
    box-shadow: 1px 2px 6px $grey05;
  }
  &__btn-img{
    width: 60px;
    height: auto;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    top: 40%;
  }
  &__modal{
    background-color: $white;
    width: 100%;
    border: none;
    color: $black;
    &:hover{
      background-color: $grey07;
      color: $black;
    }
  }
  &__form{
    .form-control{
      border: solid 1px $grey02;
    }
  }

  &__add-btn{
    color: $grey05;
  }
  &__add-btn--active{
    background-color: $l-blue;
    color: $white;
  }
  &__add-icon{
    line-height: 54px;
  }
}

.modal{
  background-color: $grey07;
  &__icon{
    display: inline-block;
    font-size: 14px;
    vertical-align: text-bottom;
    @media screen and (min-width:992px) {
      font-size: 18px;
    }
  }
}

.result-list-scroller {
  bottom: calc(env(safe-area-inset-bottom) + 160px);
  right: 22px;
}

</style>